
import { KursyPopList } from "@/kursy/pop";
import { useAuth } from "@/context/AuthContext";
import { getSubscriptionCourse } from "@/api/api";
import type { CourseKey } from "@/api/api.types";
import { useQuery } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import { useNavigate } from "@tanstack/react-router";
import { CourseAccessDrawer } from "@/components/courses/CourseAccessDrawer";
import { useCourseAccessDrawer } from "@/components/courses/useCourseAccessDrawer";
import { CourseTitleCard } from "./CourseTitleCard";


export const CoursesPopList = (): JSX.Element => {
	const { isAuthenticated } = useAuth();
	const navigate = useNavigate();
	const { state, dispatch, openAcademiaWebsite } = useCourseAccessDrawer("https://akademialtca.pl/podatki-od-podstaw/");
	const [selectedCourseKey, setSelectedCourseKey] = useState<CourseKey | null>(null);

	// Query for subscription course - only runs when authenticated and course is selected
	const { data: subscriptionData } = useQuery({
		queryKey: ["subscriptionCourse", selectedCourseKey],
		queryFn: () => getSubscriptionCourse(selectedCourseKey!),
		enabled: isAuthenticated && selectedCourseKey !== null,
	});

	const handleCourseClick = (kurs: { slug: string; title: string; description: string }): void => {
		if (!isAuthenticated) {
			// Open drawer for non-authenticated users
			dispatch({
				type: "OPEN_DRAWER",
				payload: { title: kurs.title, description: kurs.description },
			});
		} else {
			// Set the course key and let the query run
			setSelectedCourseKey(kurs.slug as CourseKey);
		}
	};

	// React to subscription data (TanStack: useEffect with data in deps; avoid side effects in render)
	useEffect((): (() => void) => {
		if (subscriptionData == null || selectedCourseKey == null) return () => undefined;

		let cancelled = false;

		const run = async (): Promise<void> => {
			if (subscriptionData.has_access) {
				await navigate({
					to: "/course/$courseKey",
					params: { courseKey: selectedCourseKey },
				});
				if (!cancelled) setSelectedCourseKey(null);
			} else {
				const course = KursyPopList.find((k) => k.slug === selectedCourseKey);
				if (course) {
					dispatch({
						type: "OPEN_DRAWER",
						payload: { title: course.title, description: course.description },
					});
					dispatch({ type: "SET_STEP", payload: "go_to_website" });
				}
				if (!cancelled) setSelectedCourseKey(null);
			}
		};

		run().catch((error) => {
			console.error("Error handling subscription response:", error);
		});

		return () => {
			cancelled = true;
		};
	}, [subscriptionData, selectedCourseKey, navigate, dispatch]);

	return (
		<main className="py-3 px-safe-offset-3 min-h-[calc(100vh-176px)] overflow-x-hidden max-w-[890px] mx-auto">
			<div className="flex flex-col gap-3">
				{KursyPopList.map((kurs) => (
					<button
						key={kurs.slug}
						onClick={() => {
							handleCourseClick(kurs);
						}}
						className="w-full text-left"
					>
						<CourseTitleCard kurs={kurs} />
					</button>
				))}
			</div>

			{/* Course Access Drawer */}
			<CourseAccessDrawer
				state={state}
				onOpenChange={(open) => {
					if (open) {
						// This shouldn't happen as we control opening
						return;
					} else {
						dispatch({ type: "CLOSE_DRAWER" });
					}
				}}
				onCloseDrawer={() => { dispatch({ type: "CLOSE_DRAWER" }); }}
				onOpenWebsite={openAcademiaWebsite}
			/>
		</main>
	);
};
